import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import NotFound from '@/pages/NotFound';
import { fetchPage } from '@/lib/siteContent';
import { renderMarkdown } from '@/lib/markdown';
import { Shimmer } from '@/components/ui';

/**
 * Admin-authored page (LK Admin → Marketing → Pages). Used for /p/:slug, and
 * by /terms, /privacy… with `fallback` = the old hardcoded page, which shows
 * until a page with that slug is published.
 */
export default function CmsPage({ slug: fixedSlug, fallback = null }) {
  const params = useParams();
  const slug = fixedSlug ?? params.slug;
  const [state, setState] = useState({ loading: true, page: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, page: null });
    fetchPage(slug)
      .then((page) => !cancelled && setState({ loading: false, page }))
      .catch(() => !cancelled && setState({ loading: false, page: null }));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (state.page) document.title = state.page.seoTitle || `${state.page.title} · LocalKonnect`;
  }, [state.page]);

  if (!state.loading && !state.page) return fallback ?? <NotFound />;

  return (
    <div className="min-h-screen flex flex-col bg-surface-muted">
      <Header />
      <main className="flex-1 py-8 sm:py-14">
        <article className="mx-auto max-w-3xl px-4 sm:px-6">
          {state.loading ? (
            <div className="lk-card space-y-3 p-6 sm:p-10" aria-busy="true">
              <Shimmer className="h-9 w-2/3" />
              <Shimmer className="h-4 w-full" />
              <Shimmer className="h-4 w-5/6" />
              <Shimmer className="h-4 w-4/6" />
            </div>
          ) : (
            <div className="lk-card animate-floatUp p-6 sm:p-10">
              <h1 className="text-h1 text-ink-900">{state.page.title}</h1>
              {state.page.updatedAt && (
                <p className="mt-2 text-sm text-ink-500">
                  Last updated{' '}
                  {new Date(state.page.updatedAt).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              )}
              <div
                className="cms-body lk-prose mt-6 border-t border-ink-200/70 pt-6"
                // Markdown is escaped before rendering; legacy HTML bodies are shown as text.
                dangerouslySetInnerHTML={{
                  __html: state.page.bodyFormat === 'markdown' ? renderMarkdown(state.page.body || '') : renderMarkdown((state.page.body || '').replace(/<[^>]+>/g, ' ')),
                }}
              />
            </div>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}
